import { toCanvas } from './feedback-draw';

/** `cancelled` = the person closed the share dialog, `failed` = no capture API or an empty frame. */
export type CaptureFailure = { readonly kind: 'cancelled' | 'failed' };

const FIRST_FRAME_TIMEOUT_MS = 3000;
const nextFrames = () => new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())));

// play() alone is not enough: Firefox and Safari resolve before the first frame exists (videoWidth 0).
function firstFrame(video: HTMLVideoElement): Promise<void> {
  const ready = () => video.readyState >= 2 && video.videoWidth > 0;
  if (ready()) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const events = ['loadeddata', 'resize', 'timeupdate'] as const;
    const finish = (error?: Error) => {
      clearTimeout(timer);
      events.forEach((name) => video.removeEventListener(name, check));
      if (error) reject(error);
      else resolve();
    };
    const check = () => ready() && finish();
    const timer = setTimeout(() => finish(new Error('empty-frame')), FIRST_FRAME_TIMEOUT_MS);
    events.forEach((name) => video.addEventListener(name, check));
  });
}

/**
 * One frame of the current tab via the screen capture API (Chromium preselects this tab).
 * `hide` takes the feedback button itself out of the picture while the frame is taken.
 */
export async function captureScreen(hide: (hidden: boolean) => void): Promise<HTMLCanvasElement | CaptureFailure> {
  if (!navigator.mediaDevices?.getDisplayMedia) return { kind: 'failed' };
  let stream: MediaStream;
  try {
    stream = await navigator.mediaDevices.getDisplayMedia({
      video: { displaySurface: 'browser' },
      audio: false,
      preferCurrentTab: true,
      selfBrowserSurface: 'include',
      surfaceSwitching: 'exclude',
    } as DisplayMediaStreamOptions);
  } catch (error) {
    return { kind: error instanceof DOMException && error.name === 'NotAllowedError' ? 'cancelled' : 'failed' };
  }
  const video = document.createElement('video');
  video.muted = true;
  video.playsInline = true;
  video.srcObject = stream;
  hide(true);
  try {
    await video.play();
    await firstFrame(video);
    await nextFrames();
    return toCanvas(video, video.videoWidth, video.videoHeight);
  } catch {
    return { kind: 'failed' };
  } finally {
    hide(false);
    stream.getTracks().forEach((track) => track.stop());
  }
}

/** A chosen image file as a canvas, or null when the browser cannot decode it. */
export function canvasFromFile(file: File): Promise<HTMLCanvasElement | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve(toCanvas(image, image.naturalWidth, image.naturalHeight));
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
}
